import { z } from 'zod';
import { FEEDBACK_KEY_PATTERN, FIELD_KEY_PATTERN, VIEWPORT_CLASSES } from '../events/journey-event.schema.js';

/** Longest period a single restitution may cover. */
export const ANALYTICS_MAX_RANGE_DAYS = 366;
/** Period applied when the query names none (functional §5.4 "période courante"). */
export const ANALYTICS_DEFAULT_RANGE_DAYS = 30;

const DAY_MS = 86_400_000;

/**
 * Combinable filters of the dashboard (functional §5.4): one query parameter per
 * criterion, every one optional, never a free query language (technical §11.2).
 * `configurationVersion` is the guidance version applied when the session was
 * measured, the axis of the before/after comparison.
 */
export const analyticsFiltersSchema = z
  .object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    journeyId: z.string().uuid().optional(),
    stepId: z.string().uuid().optional(),
    fieldKey: z.string().max(128).regex(FIELD_KEY_PATTERN).optional(),
    language: z.string().length(2).optional(),
    viewport: z.enum(VIEWPORT_CLASSES).optional(),
    configurationVersion: z.coerce.number().int().min(1).optional(),
    /** Issuing entity and announcement type (functional §5.4, §5.8): apply to the collected feedback. */
    entityKey: z.string().regex(FEEDBACK_KEY_PATTERN).optional(),
    announcementType: z.string().regex(FEEDBACK_KEY_PATTERN).optional(),
  })
  .strict()
  .superRefine((filters, context) => {
    if (!filters.from || !filters.to) return;
    const from = Date.parse(filters.from);
    const to = Date.parse(filters.to);
    if (from > to) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['from'], message: 'from must not be after to' });
    }
    if (to - from > ANALYTICS_MAX_RANGE_DAYS * DAY_MS) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: `the period may not exceed ${ANALYTICS_MAX_RANGE_DAYS} days`,
      });
    }
  });
export type AnalyticsFilters = z.infer<typeof analyticsFiltersSchema>;

/** Closed list of headline indicators; the Console owns their wording. */
export const KPI_KEYS = [
  'sessions',
  'completion_rate',
  'hesitations',
  'rage_clicks',
  'median_duration_ms',
] as const;
export type KpiKey = (typeof KPI_KEYS)[number];

export const kpiDto = z.object({
  key: z.enum(KPI_KEYS),
  value: z.number(),
  unit: z.enum(['count', 'percent', 'ms', 'score']),
  deltaRatio: z.number().nullable(),
  /** True when the sample is too small to be trusted (functional §5.4 R1). */
  lowConfidence: z.boolean(),
});
export type Kpi = z.infer<typeof kpiDto>;

/** One line of the ranking (functional §5.5): a field of a step of a journey, or a step without any field, with its measure. */
export const frictionPointDto = z.object({
  id: z.string(),
  journeyId: z.string().uuid(),
  journeyName: z.string(),
  stepId: z.string().uuid(),
  stepName: z.string(),
  fieldKey: z.string().nullable(),
  /** Label the page gives the field, as the measurement observed it; null when none was recorded. */
  fieldLabel: z.string().nullable(),
  frictionScore: z.number().int().min(0).max(100),
  affectedSessions: z.number().int(),
  impact: z.number(),
  abandonRate: z.number().min(0).max(1),
  errorRate: z.number().min(0).max(1),
  reentryRate: z.number().min(0).max(1),
  hesitationCount: z.number().int(),
  /** Median time on the field, to the resolution of the duration buckets. */
  medianDurationMs: z.number().int(),
  /** Demand score of the step the point sits on (functional §5.5), 0 without feedback. */
  demandScore: z.number().int().min(0).max(100),
  /** Friction × demand ÷ 100 (functional §5.5). */
  priorityScore: z.number().int().min(0).max(100),
  lowConfidence: z.boolean(),
  hasGuidance: z.boolean(),
});
export type FrictionPoint = z.infer<typeof frictionPointDto>;

export const frictionRankingDto = z.object({
  items: z.array(frictionPointDto),
  /** Points measured on too few sessions, shown as flagged and kept out of `items` (functional §5.4 R1). */
  excludedLowConfidence: z.number().int(),
  minimumSessionCount: z.number().int(),
});
export type FrictionRanking = z.infer<typeof frictionRankingDto>;

export const languageBreakdownDto = z.object({
  language: z.string(),
  sessions: z.number().int(),
  completions: z.number().int(),
  completionRate: z.number().min(0).max(1),
  hesitationCount: z.number().int(),
  errorCount: z.number().int(),
  lowConfidence: z.boolean(),
});
export type LanguageBreakdown = z.infer<typeof languageBreakdownDto>;

/** Values the measured data actually carries, so the filter bar offers only real options. */
export const analyticsDimensionsDto = z.object({
  languages: z.array(z.string()),
  viewports: z.array(z.enum(VIEWPORT_CLASSES)),
  configurationVersions: z.array(z.number().int()),
});
export type AnalyticsDimensions = z.infer<typeof analyticsDimensionsDto>;

export const dashboardDto = z.object({
  period: z.object({ from: z.string().datetime(), to: z.string().datetime() }),
  kpis: z.array(kpiDto),
  frictionPoints: z.array(frictionPointDto),
  journeys: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      sessionCount: z.number().int(),
      completionRate: z.number(),
    }),
  ),
  sessionsByDay: z.array(
    z.object({ day: z.string(), sessions: z.number().int(), completions: z.number().int() }),
  ),
  languages: z.array(languageBreakdownDto),
  dimensions: analyticsDimensionsDto,
  minimumSessionCount: z.number().int(),
  /**
   * When the analysis pipeline last completed a run for the project; null until
   * the very first one. The Console dates its figures with it, so an engine that
   * stopped reads as a stale dashboard instead of an absence of traffic.
   */
  lastAnalysisAt: z.string().datetime().nullable(),
});
export type Dashboard = z.infer<typeof dashboardDto>;

/** One step of the funnel (functional §5.5 "Entonnoir par sous-étape"). */
export const funnelStepDto = z.object({
  stepId: z.string().uuid(),
  stepName: z.string(),
  position: z.number().int(),
  enteredSessions: z.number().int(),
  exitedSessions: z.number().int(),
  completedSessions: z.number().int(),
  dropOffRate: z.number().min(0).max(1),
  /** Median time spent on the step, to the resolution of the duration buckets. */
  medianDurationMs: z.number().int(),
  frictionScore: z.number().int().min(0).max(100),
  hesitationCount: z.number().int(),
  rageClickCount: z.number().int(),
  errorCount: z.number().int(),
  /** Last field touched by most of the sessions that left on this step; null when none. */
  majorityExitFieldKey: z.string().nullable(),
  isMajorityExit: z.boolean(),
  lowConfidence: z.boolean(),
});
export type FunnelStep = z.infer<typeof funnelStepDto>;

export const funnelDto = z.object({
  journeyId: z.string().uuid(),
  journeyName: z.string(),
  enteredSessions: z.number().int(),
  completedSessions: z.number().int(),
  completionRate: z.number().min(0).max(1),
  majorityExitStepId: z.string().uuid().nullable(),
  steps: z.array(funnelStepDto),
});
export type Funnel = z.infer<typeof funnelDto>;

/** Indicators of one field (functional §5.5 "Indicateurs par champ"). */
export const fieldMetricDto = z.object({
  journeyId: z.string().uuid(),
  journeyName: z.string(),
  stepId: z.string().uuid(),
  stepName: z.string(),
  fieldKey: z.string(),
  /** Label the page gives the field, as the measurement observed it; null when none was recorded. */
  fieldLabel: z.string().nullable(),
  focusedSessions: z.number().int(),
  medianDurationMs: z.number().int(),
  hesitationCount: z.number().int(),
  reentryRate: z.number().min(0).max(1),
  errorRate: z.number().min(0).max(1),
  abandonRate: z.number().min(0).max(1),
  frictionScore: z.number().int().min(0).max(100),
  lowConfidence: z.boolean(),
});
export type FieldMetric = z.infer<typeof fieldMetricDto>;

export const fieldMetricsDto = z.object({
  items: z.array(fieldMetricDto),
  totalCount: z.number().int(),
});
export type FieldMetrics = z.infer<typeof fieldMetricsDto>;

/** Datasets a team can take out of the product in an open format (functional §5.4 "Extraction des données"). */
export const EXPORT_DATASETS = [
  'friction-points',
  'funnel',
  'field-metrics',
  'feedback',
  'feedback-comments',
  'comparison',
] as const;
export type ExportDataset = (typeof EXPORT_DATASETS)[number];
export const exportDatasetSchema = z.enum(EXPORT_DATASETS);

export const EXPORT_FORMATS = ['csv', 'json'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
export const exportFormatSchema = z.enum(EXPORT_FORMATS);
